import { WithStyles } from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/DeleteOutlined";
import { FORM_ERROR, FormApi, FormState } from "final-form";
import { inject, observer } from "mobx-react";
import React from "react";
import { Form, FormRenderProps } from "react-final-form";
import {
  CommonsStore,
  FieldError,
  PopupMessage
} from "../../../model/CommonsStore";
import {
  DynamicForm,
  PresentationComponentProps
} from "../../DynamicForm/DynamicForm";
import {
  SecuredButton,
  SecuredIconButton,
  SecurityProvider
} from "../../Secured";
import arrayMutators from "final-form-arrays";
import { FlatForm } from "./FlatForm";
import { TabsForm } from "./TabsForm";
import {
  DefaultContentContainer,
  DefaultTitleContainer,
  DefaultViewContainer,
  OneViewProps,
  viewStyles
} from "../OneView";
import { TitleRow } from "../TitleRow";
import { GoBackButton, I18n } from "../../../index";
import { disableable } from "../../../helpers/renderUtils";

export interface ConfirmationDialogState {
  dialogOpen: boolean;
}

export interface ShowViewProps<T, ID>
  extends OneViewProps<T, ID>,
    WithStyles<typeof viewStyles> {
  id: ID;
  hideDeleteButton?: boolean;
  customButtons?: any;
  onlyView?: boolean;
  deleteMessage?: PopupMessage;
  commons?: CommonsStore;
  i18n?: I18n;
  required?: string;
  preloadData?: false;
}

@inject("router", "commons", "i18n")
@observer
class ShowViewComponent<T extends object, ID> extends React.Component<
  ShowViewProps<T, ID>,
  ConfirmationDialogState
> {
  public constructor(props: ShowViewProps<T, ID>) {
    super(props);
    this.state = {
      dialogOpen: false
    };
  }

  public componentDidMount() {
    if (this.props.preloadData !== false) {
      this.props.dataSource.clear();
      this.props.dataSource.findOne(this.props.id).then(() => {
        this.forceUpdate();
      });
    }
  }

  public componentWillUnmount() {
    if (this.props.preloadData !== false) {
      this.props.dataSource.clear();
    }
  }

  public render() {
    const { classes, prefix, title, titleRow, titleVariant } = this.props;

    const ViewContainer = this.props.viewContainer || DefaultViewContainer;
    const TitleContainer = this.props.titleContainer || DefaultTitleContainer;
    const ContentContainer =
      this.props.contentContainer || DefaultContentContainer;

    return (
      <SecurityProvider security={this.props.dataSource.schema.security}>
        <ViewContainer>
          {titleRow !== false ? (
            <TitleContainer>
              <TitleRow
                classes={classes}
                prefix={disableable(
                  prefix,
                  <GoBackButton goBack={this.props.goBack} />
                )}
                title={title}
                titleVariant={titleVariant}
                suffix={this.getButtons()}
              />
            </TitleContainer>
          ) : null}
          <ContentContainer>
            <Form
              onSubmit={this.submit}
              render={this.dynamicForm}
              initialValues={this.props.dataSource.one}
              mutators={{ ...arrayMutators }}
            />
          </ContentContainer>
        </ViewContainer>
      </SecurityProvider>
    );
  }

  public submit = async (
    values: object,
    form: FormApi,
    callback?: (errors?: object) => void
  ) => {
    try {
      await this.props.dataSource.update();
      await this.props.dataSource.findOne(this.props.id);
      this.forceUpdate();

      if (callback) {
        callback();
      }
    } catch (e) {
      const errorObj = {};
      if (!e.response) {
        throw e;
      }

      if (e.response.data.errors) {
        e.response.data.errors.forEach((error: FieldError) => {
          errorObj[error.fieldName] = error.message;
        });
      } else {
        errorObj[FORM_ERROR] = e.response.data.message;
      }
      callback!(errorObj);
    }
  };

  public delete = async () => {
    await this.props.dataSource.delete();
    this.props.commons!.closeDialog();

    if (this.props.goBack) {
      this.props.goBack();
    } else {
      this.props.router!.goToParent();
    }
  };

  public openDialog = () => {
    const i18n = this.props?.i18n as I18n;
    let dialogMessage: PopupMessage = {
      title: i18n.t("lego:views.show.confirmDelete.title", "Confirm Deleting"),
      content: i18n.t(
        "lego:views.show.confirmDelete.content",
        "Are you sure you want to delete this entry?"
      )
    };
    if (this.props.deleteMessage) {
      dialogMessage = this.props.deleteMessage;
    }
    this.props.commons!.showDialog(dialogMessage, this.delete);
  };

  public dynamicForm = (formRenderProps: FormRenderProps<any>) => {
    const i18n = this.props?.i18n as I18n;
    return (
      <DynamicForm
        {...formRenderProps}
        fields={this.props.fields}
        Presentation={this.pickPresentationFormat()}
        // @ts-ignore
        values={formRenderProps.values}
        model={this.props.dataSource.one}
        onChange={this.changed}
      >
        {this.props.onlyView ? (
          <></>
        ) : (
          <SecuredButton
            type="submit"
            variant="contained"
            color="primary"
            // @ts-ignore
            disabled={formRenderProps.hasValidationErrors}
            className={this.props.classes.submitButton}
          >
            {i18n.t("commons:save", "Save")}
          </SecuredButton>
        )}
      </DynamicForm>
    );
  };

  private pickPresentationFormat(): React.FunctionComponent<
    PresentationComponentProps
  > {
    if (this.props.Presentation) {
      return this.props.Presentation;
    }

    if (!this.props.fields.fields[0].group) {
      return FlatForm;
    }

    return TabsForm;
  }

  // @ts-ignore
  private changed = (formState: FormState<any>) => {
    this.removeErrorMessage(formState);
    this.props.dataSource.change(formState.values as T);
  };

  private removeErrorMessage(formState: FormState<Object>) {
    if (formState.active && formState.submitErrors) {
      formState.submitErrors[formState.active] = null;
    }
  }

  private getButtons = () => {
    return (
      <>
        {this.props.customButtons}
        {!this.props.hideDeleteButton && this.getDeleteButton()}
      </>
    );
  };

  private getDeleteButton = () => {
    return (
      <Tooltip title="Delete">
        <SecuredIconButton
          requiredAccess={this.props.required}
          onClick={this.openDialog}
          style={{ float: "right" }}
          color="primary"
        >
          <DeleteIcon />
        </SecuredIconButton>
      </Tooltip>
    );
  };
}

export const ShowView = withStyles(viewStyles, { withTheme: true })(
  ShowViewComponent
);
